// src/pages/Contacts/components/ContactDetailModal.tsx
import React from "react";
import { X, Edit, Trash2, Calendar, User, Mail, Activity } from "lucide-react";
import { formatDate } from "../../../utils/formatters";
import type { ContactResponseDto } from "../../../types/contact";

interface ContactDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (contact: ContactResponseDto) => void;
  onDelete?: (contact: ContactResponseDto) => void;
  contact: ContactResponseDto | null;
}

const ContactDetailModal: React.FC<ContactDetailModalProps> = ({
  isOpen,
  onClose,
  onEdit,
  onDelete,
  contact,
}) => {
  if (!isOpen || !contact) return null;

  const handleEdit = () => {
    onEdit?.(contact);
    onClose();
  };

  const handleDelete = () => {
    onDelete?.(contact);
    onClose();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "inactive":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const isUrl = (str: string) => {
    try {
      new URL(str);
      return true;
    } catch {
      return false;
    }
  };

  const isEmail = (str: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);
  };

  const isPhone = (str: string) => {
    return /^[\+]?[\d\s\-\(\)]+$/.test(str);
  };

  const renderContactInfo = (contactInfo: string) => {
    if (isUrl(contactInfo)) {
      return (
        <a
          href={contactInfo}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline break-all"
        >
          {contactInfo}
        </a>
      );
    } else if (isEmail(contactInfo)) {
      return (
        <a
          href={`mailto:${contactInfo}`}
          className="text-blue-600 hover:text-blue-800 underline break-all"
        >
          {contactInfo}
        </a>
      );
    } else if (isPhone(contactInfo)) {
      return (
        <a
          href={`tel:${contactInfo}`}
          className="text-blue-600 hover:text-blue-800 underline"
        >
          {contactInfo}
        </a>
      );
    } else {
      return <span className="break-all">{contactInfo}</span>;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Contact Details
              </h3>
              <div className="flex items-center space-x-2">
                {onEdit && (
                  <button
                    type="button"
                    onClick={handleEdit}
                    className="text-blue-600 hover:text-blue-800 p-1"
                    title="Edit Contact"
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                )}
                {onDelete && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="text-red-600 hover:text-red-800 p-1"
                    title="Delete Contact"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                )}
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600 p-1"
                  onClick={onClose}
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 gap-4">
                <div className="flex items-center">
                  <User className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="text-base font-medium text-gray-900">{contact.name}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Mail className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">Contact Information</p>
                    <p className="text-base text-gray-900">
                      {renderContactInfo(contact.contact)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <Activity className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                        contact.status
                      )}`}
                    >
                      {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Metadata */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Details</h4>
                <div className="grid grid-cols-1 gap-3">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                    <div>
                      <p className="text-xs text-gray-500">Created</p>
                      <p className="text-sm text-gray-900">
                        {formatDate(contact.createdAt)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                    <div>
                      <p className="text-xs text-gray-500">Last Updated</p>
                      <p className="text-sm text-gray-900">
                        {formatDate(contact.updatedAt)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <User className="h-4 w-4 text-gray-400 mr-2" />
                    <div>
                      <p className="text-xs text-gray-500">Contact ID</p>
                      <p className="text-sm text-gray-900">#{contact.id}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactDetailModal;